import React from "react"
import styled from "@emotion/styled"
import { AnimatePresence, motion } from "framer-motion"
import Section from "src/components/Section"
import Grid from "src/components/Grid"
import Image from "src/components/Image"
import TextLockup from "src/components/TextLockup"
import { mq, globals, colors, typography } from "src/styles"
import { useState } from "react"
import { css } from "@emotion/react"

const WrapperSection = styled(Section)`
  padding-left: 20px;
  padding-right: 20px;
  ${mq.extraLargeAndUp} {
    padding-left: 0;
    padding-right: 0;
  }
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => (hideMobile && "display: none")}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => (hideDesktop && "display: none")}
  }
  ${({ isFirstSection }) => (isFirstSection ? "" : "")}
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
  `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
`

const Inner = styled.div`
  max-width: 1536px;
  margin: 0 auto;

  h2 {
    margin-bottom: 20px;
  }
`

const MerchantsContainer = styled.div`
  position: relative;
  margin-top: 20px;
`

const ColumnWrapper = styled.div`
  h1,
  h2 {
    max-width: 9em;
  }
  h3 {
    max-width: 11em;
  }
  h4,
  h5 {
    max-width: 24em;
  }
  h6 {
    max-width: 16em;
  }
  p {
    max-width: 40em;
  }
`

const Logo = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  > div {
    display: flex;
    height: 100%;

    img {
      object-fit: contain !important;
    }
  }
`

const Menu = styled.menu`
  display: flex;
  flex-shrink: 0;
  width: 100%;
  align-items: center;
  justify-content: space-around;
  border-bottom: 1px solid ${colors.hrColor};
  padding-top: 10px;
  padding-bottom: 10px;

  gap: 12px;

  ${mq.largeAndUp} {
    padding-right: 8px;
    padding-left: 8px;
    gap: 36px;
  }

  > button {
    width: 100%;
    height: 45px;
  }
`

const MerchantTitle = styled.h3`
  ${typography.h1Light}
`

const Button = styled.button`
  border: none;
  cursor: pointer;
  background: ${colors.lightGrey};
  padding: 4px;
  ${({ active }) => (active ? "opacity: 100%" : `opacity: 40%`)}
`

const StyledGrid = styled(motion.div)`
  transform-origin: bottom center;
  display: flex;
  flex-direction: column;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  gap: 20px;

  ${mq.largeAndUp} {
    flex-direction: row;
  }

  > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  > div:nth-child(1) {
    width: 100%;
    height: auto;
    * {
      margin-top: 0 !important;
    }
    p {
      margin-bottom: 0.75em;
    }
    ${mq.largeAndUp} {
      width: 40%;
    }
  }
  > div:nth-child(2),
  > div:nth-child(3) {
    width: 100%;
    height: auto;
    ${mq.largeAndUp} {
      width: 60%;
    }
  }

  .image-mobile {
    display: block !important;
    ${mq.largeAndUp} {
      display: none !important;
    }
  }

  .image-desktop {
    display: none !important;
    ${mq.largeAndUp} {
      display: block !important;
    }
  }
`

const Merchants = ({
  isFirstSection,
  className,
  theme,
  spacing,
  prevTheme,
  nextTheme,
  headingSize,
  headline,
  merchants,
  id,
  hidden,
  hideMobile,
  hideDesktop,
}) => {
  if (hidden) return null

  const [activeSlide, setActiveSlide] = useState(0)

  const sizes = "(min-width: " + mq.mediumBreakpoint + "px) 50vw, 100vw"

  return (
    <WrapperSection
      className={className}
      setTheme={theme}
      prevTheme={prevTheme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      id={id}
      vertical={spacing?.vertical}
      horizontal={spacing?.horizontal}
      hideMobile={hideMobile}
      hideDesktop={hideDesktop}
    >
      <Inner>
        {headline && (
          <Grid small="[1]" vAlign="top">
            <div>
              <h2 className={headingSize ? headingSize : "h3"}>{headline}</h2>
            </div>
          </Grid>
        )}
        <Grid small="[1]" vAlign="top">
          <ColumnWrapper>
            <Menu>
              {merchants.map((m, idx) => (
                <Button
                  key={m._key}
                  onClick={() => setActiveSlide(idx)}
                  title={m.title}
                  active={idx === activeSlide}
                >
                  <Logo>
                    <Image
                      alt={m.title}
                      image={m?.logo?.asset?.gatsbyImageData}
                      sizes={"400px"}
                    />
                  </Logo>
                </Button>
              ))}
            </Menu>
            <MerchantsContainer>
              <AnimatePresence initial={false} mode="wait">
                {merchants.map(
                  (m, idx) =>
                    idx === activeSlide && (
                      <StyledGrid
                        key={m._key}
                        title={m.title}
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{
                          duration: 0.35,
                        }}
                      >
                        <div>
                          <MerchantTitle>{m.title}</MerchantTitle>
                          <TextLockup
                            entranceDelay={0.5}
                            text={m._rawText}
                            actions={m.actions}
                            theme={theme}
                            transitionIn={true}
                          />
                        </div>

                        <Image
                          className="image-desktop"
                          image={m?.media?.image?.asset?.gatsbyImageData}
                          altText={m.title}
                          width={515}
                          height={365}
                          ratio={365 / 515}
                          sizes={sizes}
                        />
                        <Image
                          className="image-mobile"
                          image={m?.media?.image?.asset?.gatsbyImageData}
                          altText={m.title}
                          width={365}
                          height={515}
                          ratio={515 / 365}
                          sizes={sizes}
                        />
                      </StyledGrid>
                    )
                )}
              </AnimatePresence>
            </MerchantsContainer>
          </ColumnWrapper>
        </Grid>
      </Inner>
    </WrapperSection>
  )
}

export default Merchants
